import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import PageShell from '@components/pages/PageShell';
import Newsletter from '@components/landing/Newsletter';
import { Lightbox, type LightboxContent } from '@components/pages/ResourceActions';
import { CONTACT, FAQS, HELP_INTRO_REF, PRIVACY_POLICY, TERMS, TUTORIALS, type LegalDocument } from '@/data/help';

type SectionId = 'contact' | 'faqs' | 'terms' | 'tutorials' | 'privacy';

const SECTIONS: { id: SectionId; label: string; icon: React.ReactNode }[] = [
    { id: 'contact', label: 'Contact Us', icon: <HeadsetIcon /> },
    { id: 'faqs', label: 'FAQs', icon: <QuestionIcon /> },
    { id: 'terms', label: 'Terms & Condition', icon: <DocumentIcon /> },
    { id: 'tutorials', label: 'Tutorials', icon: <PlayIcon /> },
    { id: 'privacy', label: 'Privacy Policy', icon: <ShieldIcon /> },
];

export default function Help() {
    const [params, setParams] = useSearchParams();
    const requested = params.get('section') as SectionId | null;
    const active: SectionId = SECTIONS.some((s) => s.id === requested) ? requested! : 'contact';

    const [query, setQuery] = useState('');
    const [openFaq, setOpenFaq] = useState<number | null>(0);
    const [preview, setPreview] = useState<LightboxContent | null>(null);

    const select = (id: SectionId) => {
        setParams(id === 'contact' ? {} : { section: id }, { replace: true });
        setQuery('');
    };

    /** Search runs across FAQs and tutorials — the two things worth finding. */
    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return null;
        return {
            faqs: FAQS.filter((f) => `${f.question} ${f.answer}`.toLowerCase().includes(q)),
            tutorials: TUTORIALS.filter((t) => t.title.toLowerCase().includes(q)),
        };
    }, [query]);

    const current = SECTIONS.find((s) => s.id === active)!;

    return (
        <PageShell>
            {/* ── Hero ─────────────────────────────────────────────────── */}
            <section className="bg-white pt-[120px] lg:pt-[150px]">
                <div className="mx-auto max-w-[1640px] px-5 lg:px-[140px]">
                    <div className="rounded-[20px] bg-[#f4f4f4] px-6 py-10 lg:px-14 lg:py-14">
                        <h1 className="font-primary text-[46px] font-bold leading-[1.05] tracking-[-2px] text-[#08080c] lg:text-[72px] lg:tracking-[-3px]">
                            Help Center
                        </h1>

                        <div className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between lg:gap-16">
                            <p className="max-w-[720px] text-[13px] leading-[1.8] text-[#5e5e5e] lg:text-[14px]">
                                {HELP_INTRO_REF.text}
                            </p>

                            <label className="relative w-full lg:w-[720px]">
                                <span className="sr-only">Search the help centre</span>
                                <input
                                    type="search"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search"
                                    className="h-[52px] w-full rounded-[10px] bg-white px-5 pr-12 text-[14px] text-[#151515] outline-none placeholder:text-[#9a9a9a]"
                                />
                                <svg
                                    className="pointer-events-none absolute right-4 top-1/2 size-5 -translate-y-1/2 text-[#5e5e5e]"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    aria-hidden
                                >
                                    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                    <path
                                        d="M20 20l-3.5-3.5"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </label>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Body ─────────────────────────────────────────────────── */}
            <section className="bg-white pb-14 pt-10 lg:pb-20 lg:pt-12">
                <div className="mx-auto flex max-w-[1640px] flex-col gap-8 px-5 lg:flex-row lg:gap-16 lg:px-[140px]">
                    {/* Section nav */}
                    <nav aria-label="Help sections" className="lg:w-[260px] lg:shrink-0">
                        <ul className="flex flex-col gap-1">
                            {SECTIONS.map((s) => {
                                const on = s.id === active;
                                return (
                                    <li key={s.id}>
                                        <button
                                            type="button"
                                            onClick={() => select(s.id)}
                                            aria-current={on}
                                            className={`flex w-full items-center gap-3 rounded-[8px] px-4 py-3 text-left text-[14px] font-semibold transition-colors ${
                                                on ? 'bg-[#08080c] text-white' : 'text-[#08080c] hover:bg-[#f4f4f4]'
                                            }`}
                                        >
                                            <span className={on ? 'text-white' : 'text-[#5e5e5e]'}>{s.icon}</span>
                                            {s.label}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </nav>

                    {/* Panel */}
                    <div className="min-w-0 flex-1">
                        {results ? (
                            <SearchResults
                                results={results}
                                query={query}
                                onOpenFaq={(i) => {
                                    setQuery('');
                                    select('faqs');
                                    setOpenFaq(i);
                                }}
                                onOpenTutorial={(t) =>
                                    setPreview({ title: t.title, image: t.thumbnail, videoUrl: t.videoUrl })
                                }
                            />
                        ) : (
                            <>
                                <p className="flex items-center gap-3 text-[15px] font-bold text-[#08080c]">
                                    <span className="flex size-8 items-center justify-center rounded-[6px] bg-[#fff0e5] text-[#f60]">
                                        {current.icon}
                                    </span>
                                    {current.label}
                                </p>

                                <div className="mt-8">
                                    {active === 'contact' && <ContactPanel />}
                                    {active === 'faqs' && (
                                        <FaqPanel
                                            open={openFaq}
                                            onToggle={(i) => setOpenFaq(openFaq === i ? null : i)}
                                        />
                                    )}
                                    {active === 'tutorials' && (
                                        <TutorialPanel
                                            onOpen={(t) =>
                                                setPreview({ title: t.title, image: t.thumbnail, videoUrl: t.videoUrl })
                                            }
                                        />
                                    )}
                                    {active === 'terms' && <LegalPanel doc={TERMS} />}
                                    {active === 'privacy' && <LegalPanel doc={PRIVACY_POLICY} />}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </section>

            <Lightbox item={preview} onClose={() => setPreview(null)} />
            <Newsletter />
        </PageShell>
    );
}

/* ── Panels ───────────────────────────────────────────────────────────── */

function ContactPanel() {
    return (
        <div className="mx-auto max-w-[880px] text-center">
            <span className="inline-flex rounded-[6px] bg-[#f0f0f0] px-3 py-1 text-[12px] font-semibold text-[#5e5e5e]">
                Question?
            </span>
            <h2 className="font-primary mt-4 text-[26px] font-bold tracking-[-0.8px] text-[#08080c] lg:text-[32px]">
                {CONTACT.heading}
            </h2>
            <p className="mt-2 text-[13px] leading-[1.7] text-[#5e5e5e]">{CONTACT.body}</p>

            <div className="mt-10 grid gap-6 sm:grid-cols-2">
                <a
                    href={`tel:${CONTACT.phone.replace(/[^\d+]/g, '')}`}
                    className="rounded-[10px] bg-[#f4f4f4] px-6 py-10 transition-colors hover:bg-[#efefef]"
                >
                    <span className="mx-auto flex size-11 items-center justify-center rounded-[8px] bg-[#e6e6e6] text-[#08080c]">
                        <PhoneIcon />
                    </span>
                    <p className="mt-5 text-[20px] font-semibold text-[#08080c]">{CONTACT.phone}</p>
                    <p className="mt-1 text-[13px] text-[#5e5e5e]">{CONTACT.phoneNote}</p>
                </a>

                <a
                    href={`mailto:${CONTACT.email}`}
                    className="rounded-[10px] bg-[#f4f4f4] px-6 py-10 transition-colors hover:bg-[#efefef]"
                >
                    <span className="mx-auto flex size-11 items-center justify-center rounded-[8px] bg-[#e6e6e6] text-[#08080c]">
                        <MailIcon />
                    </span>
                    <p className="mt-5 text-[20px] font-semibold text-[#08080c]">{CONTACT.email}</p>
                    <p className="mt-1 text-[13px] text-[#5e5e5e]">{CONTACT.emailNote}</p>
                </a>
            </div>

            <p className="mt-8 rounded-[10px] bg-[#fff4ec] px-5 py-4 text-[13px] leading-[1.7] text-[#8a4b00]">
                Have a live legal matter? Use the legal aid form rather than this page — those requests are read first.
            </p>
        </div>
    );
}

function FaqPanel({ open, onToggle }: { open: number | null; onToggle: (i: number) => void }) {
    return (
        <ul className="flex flex-col">
            {FAQS.map((faq, i) => {
                const on = open === i;
                return (
                    <li key={faq.question} className="border-b border-[#e2e2e2]">
                        <button
                            type="button"
                            onClick={() => onToggle(i)}
                            aria-expanded={on}
                            className="flex w-full items-start gap-4 py-5 text-left"
                        >
                            <span
                                className={`tabular shrink-0 text-[14px] font-bold ${on ? 'text-[#f60]' : 'text-[#08080c]'}`}
                            >
                                {String(i + 1).padStart(2, '0')}.
                            </span>
                            <span className="min-w-0 flex-1 text-[15px] font-bold text-[#08080c]">{faq.question}</span>
                            <span className="mt-1 shrink-0 text-[#5e5e5e]">{on ? <MinusIcon /> : <PlusIcon />}</span>
                        </button>
                        {on && (
                            <p className="pb-5 pl-[46px] pr-10 text-[13px] leading-[1.8] text-[#5e5e5e]">
                                {faq.answer}
                            </p>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

function TutorialPanel({ onOpen }: { onOpen: (t: (typeof TUTORIALS)[number]) => void }) {
    return (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TUTORIALS.map((t) => (
                <button key={t.slug} type="button" onClick={() => onOpen(t)} className="group text-left">
                    <span className="relative block h-[150px] w-full overflow-hidden rounded-[8px] bg-[#d9d9d9]">
                        <img
                            src={t.thumbnail}
                            alt=""
                            className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                        />
                        <span className="absolute inset-0 bg-black/25" />
                        <span className="absolute left-1/2 top-1/2 flex size-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-[#08080c] transition-transform duration-300 group-hover:scale-110">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                                <path d="M8 5.5v13l11-6.5-11-6.5Z" />
                            </svg>
                        </span>
                        <span className="absolute bottom-2 right-2 rounded-[4px] bg-black/70 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                            {t.duration}
                        </span>
                    </span>
                    <span className="mt-3 block h-[3px] w-full rounded-full bg-[#e2e2e2]">
                        <span className="block h-full w-1/4 rounded-full bg-[#f60]" />
                    </span>
                    <span className="mt-3 block text-[14px] font-bold leading-[1.5] text-[#08080c]">{t.title}</span>
                </button>
            ))}
        </div>
    );
}

function LegalPanel({ doc }: { doc: LegalDocument }) {
    return (
        <article className="max-w-[900px]">
            <h2 className="font-primary text-[34px] font-bold leading-[1.15] tracking-[-1.2px] text-[#08080c] lg:text-[44px]">
                {doc.title}
            </h2>
            <p className="mt-2 text-[12px] text-[#9a9a9a]">Last updated {doc.updated}</p>

            {doc.sections.map((section) => (
                <section key={section.heading} className="mt-8">
                    <h3 className="text-[16px] font-bold text-[#08080c]">{section.heading}</h3>
                    {section.paragraphs.map((p, i) => (
                        <p key={i} className="mt-3 text-[13px] leading-[1.9] text-[#5e5e5e] lg:text-[14px]">
                            {p}
                        </p>
                    ))}
                </section>
            ))}
        </article>
    );
}

function SearchResults({
    results,
    query,
    onOpenFaq,
    onOpenTutorial,
}: {
    results: { faqs: typeof FAQS; tutorials: typeof TUTORIALS };
    query: string;
    onOpenFaq: (index: number) => void;
    onOpenTutorial: (t: (typeof TUTORIALS)[number]) => void;
}) {
    const total = results.faqs.length + results.tutorials.length;

    return (
        <div>
            <p className="text-[13px] text-[#5e5e5e]">
                {total} result{total === 1 ? '' : 's'} for <span className="font-bold text-[#08080c]">“{query}”</span>
            </p>

            {total === 0 && (
                <p className="mt-6 rounded-[10px] border border-dashed border-[#d0d0d0] px-6 py-10 text-center text-[13px] text-[#5e5e5e]">
                    Nothing matched. Try a different word, or contact us and we will answer directly.
                </p>
            )}

            {results.faqs.length > 0 && (
                <ul className="mt-6 flex flex-col">
                    {results.faqs.map((faq) => (
                        <li key={faq.question} className="border-b border-[#e2e2e2]">
                            <button
                                type="button"
                                onClick={() => onOpenFaq(FAQS.indexOf(faq))}
                                className="w-full py-4 text-left"
                            >
                                <span className="block text-[14px] font-bold text-[#08080c]">{faq.question}</span>
                                <span className="mt-1 line-clamp-2 block text-[13px] leading-[1.7] text-[#5e5e5e]">
                                    {faq.answer}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {results.tutorials.length > 0 && (
                <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {results.tutorials.map((t) => (
                        <button key={t.slug} type="button" onClick={() => onOpenTutorial(t)} className="text-left">
                            <span className="block h-[120px] w-full overflow-hidden rounded-[8px] bg-[#d9d9d9]">
                                <img src={t.thumbnail} alt="" className="size-full object-cover" />
                            </span>
                            <span className="mt-2 block text-[13px] font-bold text-[#08080c]">{t.title}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ── Icons ────────────────────────────────────────────────────────────── */

const S = { 'width': 18, 'height': 18, 'viewBox': '0 0 24 24', 'fill': 'none', 'aria-hidden': true } as const;
const stroke = { stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;

function HeadsetIcon() {
    return (
        <svg {...S}>
            <path d="M4 14v-2a8 8 0 1 1 16 0v2" {...stroke} />
            <path
                d="M4 14h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-4Zm16 0h-2a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-4Z"
                {...stroke}
            />
        </svg>
    );
}
function QuestionIcon() {
    return (
        <svg {...S}>
            <path d="M9.5 9a2.5 2.5 0 1 1 3.2 2.4c-.7.2-1.2.9-1.2 1.6v.5M12 17.5v.01" {...stroke} />
        </svg>
    );
}
function DocumentIcon() {
    return (
        <svg {...S}>
            <path d="M6 3h8l4 4v14H6V3Z" {...stroke} />
            <path d="M14 3v4h4M9 12h6M9 16h4" {...stroke} />
        </svg>
    );
}
function PlayIcon() {
    return (
        <svg {...S}>
            <path d="M7 5.5v13l11-6.5-11-6.5Z" {...stroke} />
        </svg>
    );
}
function ShieldIcon() {
    return (
        <svg {...S}>
            <path d="M12 3l7 3v6c0 4.2-2.9 7.9-7 9-4.1-1.1-7-4.8-7-9V6l7-3Z" {...stroke} />
            <path d="M12 11v3m0-6.5v.01" {...stroke} />
        </svg>
    );
}
function PhoneIcon() {
    return (
        <svg {...S} width={20} height={20}>
            <path
                d="M5 4h3l1.5 4-2 1.5a11 11 0 0 0 5 5L14 12l4 1.5v3a2 2 0 0 1-2.2 2A15 15 0 0 1 3 6.2 2 2 0 0 1 5 4Z"
                {...stroke}
            />
        </svg>
    );
}
function MailIcon() {
    return (
        <svg {...S} width={20} height={20}>
            <rect x="3" y="5" width="18" height="14" rx="2" {...stroke} />
            <path d="m3.5 7 8.5 6 8.5-6" {...stroke} />
        </svg>
    );
}
function PlusIcon() {
    return (
        <svg {...S} width={16} height={16}>
            <path d="M12 5v14M5 12h14" {...stroke} />
        </svg>
    );
}
function MinusIcon() {
    return (
        <svg {...S} width={16} height={16}>
            <path d="M5 12h14" {...stroke} />
        </svg>
    );
}
